'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Camera {
  id: number;
  label: string;
  location: string;
}

export const CAMERAS: Camera[] = [
  { id: 1,  label: 'Camera 1',  location: 'Main Stage'       },
  { id: 2,  label: 'Camera 2',  location: 'Audience Left'    },
  { id: 3,  label: 'Camera 3',  location: 'Audience Right'   },
  { id: 4,  label: 'Camera 4',  location: 'Backstage'        },
  { id: 5,  label: 'Camera 5',  location: 'Entrance Gate'    },
  { id: 6,  label: 'Camera 6',  location: 'VIP Lounge'       },
  { id: 7,  label: 'Camera 7',  location: 'Press Area'       },
  { id: 8,  label: 'Camera 8',  location: 'Overhead Wide'    },
  { id: 9,  label: 'Camera 9',  location: 'Side Stage Left'  },
  { id: 10, label: 'Camera 10', location: 'Side Stage Right' },
];

export interface ReconnectToast {
  id: string;
  cameraId: number;
  cameraLabel: string;
  secondsLeft: number;
  attempt: number;
  maxAttempts: number;
  failed: boolean;
}

interface WebRTCContextValue {
  remoteStreams: Map<number, MediaStream>;
  liveCameras: Set<number>;
  mutedCameras: Set<number>;
  cameraVolumes: Record<number, number>;
  toasts: ReconnectToast[];
  nowLiveCamera: number;
  dismissToast: (id: string) => void;
  toggleMute: (id: number) => void;
  setVolume: (id: number, value: number) => void;
  setNowLiveCamera: (id: number) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const WebRTCContext = createContext<WebRTCContextValue>({
  remoteStreams: new Map(),
  liveCameras: new Set(),
  mutedCameras: new Set(),
  cameraVolumes: {},
  toasts: [],
  nowLiveCamera: 1,
  dismissToast: () => {},
  toggleMute: () => {},
  setVolume: () => {},
  setNowLiveCamera: () => {},
});

export const useWebRTC = () => useContext(WebRTCContext);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WebRTCProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [remoteStreams, setRemoteStreams] = useState<Map<number, MediaStream>>(new Map());
  const [liveCameras, setLiveCameras] = useState<Set<number>>(new Set());
  const [mutedCameras, setMutedCameras] = useState<Set<number>>(new Set());
  const [cameraVolumes, setCameraVolumes] = useState<Record<number, number>>({});
  const [toasts, setToasts] = useState<ReconnectToast[]>([]);
  const [nowLiveCamera, setNowLiveCameraState] = useState<number>(1);

  // Stable supabase client — never recreated across renders/navigations
  const supabaseRef = useRef(createClient());
  const myId = useRef(`admin-${Math.random().toString(36).slice(2, 8)}`);
  const peerConnectionsRef = useRef<Map<number, RTCPeerConnection>>(new Map());
  const channelsRef = useRef<Map<number, ReturnType<ReturnType<typeof createClient>['channel']>>>(new Map());
  const retryStateRef = useRef<Map<number, { attempt: number; timeoutId: ReturnType<typeof setTimeout> | null }>>(new Map());
  const sendPingRef = useRef<Map<number, (cameraInstanceId: string) => void>>(new Map());
  // Countdown interval refs per camera
  const countdownIntervalsRef = useRef<Map<number, ReturnType<typeof setInterval>>>(new Map());
  // Track whether we've already initialised — never reset on navigation, only on logout
  const initialisedRef = useRef(false);
  const prevUserRef = useRef<string | null>(null);

  // ─── Stable ref for nowLiveCamera — used by relay without re-subscribing ───
  const nowLiveCameraRef = useRef<number>(1);

  // ─── Stable ref for remoteStreams — relay reads this to avoid stale closure ──
  const remoteStreamsRef = useRef<Map<number, MediaStream>>(new Map());

  // ─── Relay peer connections: viewerId → RTCPeerConnection ───────────────────
  const relayPeersRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  // ─── ICE candidate queue per relay viewer (before setRemoteDescription) ─────
  const relayIceCandidateQueueRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  // ─── watch-live-signal channel ref ──────────────────────────────────────────
  const watchLiveChannelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  // ─── FIX 1: ICE candidate queue per camera ──────────────────────────────────
  // Buffers ICE candidates that arrive before setRemoteDescription completes
  const iceCandidateQueueRef = useRef<Map<number, RTCIceCandidateInit[]>>(new Map());

  // ─── FIX 2: Negotiation guard per camera ────────────────────────────────────
  // Prevents two concurrent offer/answer flows on the same camera channel
  const isNegotiatingRef = useRef<Map<number, boolean>>(new Map());

  // ─── Toast helpers ──────────────────────────────────────────────────────────

  const upsertToast = useCallback((toast: ReconnectToast) => {
    setToasts((prev) => {
      const idx = prev.findIndex((t) => t.cameraId === toast.cameraId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = toast;
        return next;
      }
      return [...prev, toast];
    });
  }, []);

  const removeToastForCamera = useCallback((cameraId: number) => {
    setToasts((prev) => prev.filter((t) => t.cameraId !== cameraId));
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const startCountdown = useCallback(
    (cameraId: number, totalSeconds: number, attempt: number, maxAttempts: number) => {
      const existing = countdownIntervalsRef.current.get(cameraId);
      if (existing) clearInterval(existing);

      const camera = CAMERAS.find((c) => c.id === cameraId);
      const cameraLabel = camera ? camera.label : `Camera ${cameraId}`;
      const toastId = `reconnect-${cameraId}`;

      let secondsLeft = totalSeconds;

      upsertToast({
        id: toastId,
        cameraId,
        cameraLabel,
        secondsLeft,
        attempt,
        maxAttempts,
        failed: false,
      });

      const intervalId = setInterval(() => {
        secondsLeft -= 1;
        if (secondsLeft <= 0) {
          clearInterval(intervalId);
          countdownIntervalsRef.current.delete(cameraId);
          return;
        }
        upsertToast({
          id: toastId,
          cameraId,
          cameraLabel,
          secondsLeft,
          attempt,
          maxAttempts,
          failed: false,
        });
      }, 1000);

      countdownIntervalsRef.current.set(cameraId, intervalId);
    },
    [upsertToast]
  );

  const showFailedToast = useCallback(
    (cameraId: number, maxAttempts: number) => {
      const existing = countdownIntervalsRef.current.get(cameraId);
      if (existing) {
        clearInterval(existing);
        countdownIntervalsRef.current.delete(cameraId);
      }
      const camera = CAMERAS.find((c) => c.id === cameraId);
      const cameraLabel = camera ? camera.label : `Camera ${cameraId}`;
      upsertToast({
        id: `reconnect-${cameraId}`,
        cameraId,
        cameraLabel,
        secondsLeft: 0,
        attempt: maxAttempts,
        maxAttempts,
        failed: true,
      });
      setTimeout(() => removeToastForCamera(cameraId), 8000);
    },
    [upsertToast, removeToastForCamera]
  );

  // ─── Teardown ───────────────────────────────────────────────────────────────

  const teardown = useCallback(() => {
    retryStateRef.current.forEach((entry) => {
      if (entry.timeoutId) clearTimeout(entry.timeoutId);
    });
    retryStateRef.current.clear();
    sendPingRef.current.clear();
    countdownIntervalsRef.current.forEach((id) => clearInterval(id));
    countdownIntervalsRef.current.clear();
    channelsRef.current.forEach((ch) => ch.unsubscribe());
    channelsRef.current.clear();
    peerConnectionsRef.current.forEach((pc) => pc.close());
    peerConnectionsRef.current.clear();
    iceCandidateQueueRef.current.clear();
    isNegotiatingRef.current.clear();
    initialisedRef.current = false;
    setToasts([]);
  }, []);

  // ─── Channel / PeerConnection setup ─────────────────────────────────────────

  const setupCameraChannel = useCallback(
    (cameraId: number) => {
      if (channelsRef.current.has(cameraId)) return;

      const supabase = supabaseRef.current;
      const channelName = `camera-signal-${cameraId}`;
      const channel = supabase.channel(channelName, {
        config: { broadcast: { self: false } },
      });

      const cameraInstanceIdRef = { current: '' };

      const createPeerConnection = (camId: number): RTCPeerConnection => {
        // Close and remove any existing PC first
        if (peerConnectionsRef.current.has(camId)) {
          const old = peerConnectionsRef.current.get(camId)!;
          old.ontrack = null;
          old.onicecandidate = null;
          old.onconnectionstatechange = null;
          old.close();
          peerConnectionsRef.current.delete(camId);
        }

        // Clear ICE queue and negotiation guard for fresh start
        iceCandidateQueueRef.current.set(camId, []);
        isNegotiatingRef.current.set(camId, false);

        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' },
            {
              urls: 'turn:openrelay.metered.ca:80',
              username: 'openrelayproject',
              credential: 'openrelayproject',
            },
            {
              urls: 'turn:openrelay.metered.ca:443',
              username: 'openrelayproject',
              credential: 'openrelayproject',
            },
            {
              urls: 'turn:openrelay.metered.ca:443?transport=tcp',
              username: 'openrelayproject',
              credential: 'openrelayproject',
            },
          ],
          iceCandidatePoolSize: 10,
        });

        pc.ontrack = (event) => {
          const stream = event.streams[0];
          if (stream) {
            setRemoteStreams((prev) => {
              const next = new Map(prev);
              next.set(camId, stream);
              remoteStreamsRef.current = next;
              return next;
            });
            setLiveCameras((prev) => new Set([...prev, camId]));
          }
        };

        pc.onicecandidate = (event) => {
          if (event.candidate && cameraInstanceIdRef.current) {
            channel.send({
              type: 'broadcast',
              event: 'signal',
              payload: {
                type: 'ice-candidate',
                candidate: event.candidate.toJSON(),
                from: myId.current,
                to: cameraInstanceIdRef.current,
              },
            });
          }
        };

        pc.onconnectionstatechange = () => {
          const state = pc.connectionState;
          console.log(`[admin] Camera ${camId} connection state: ${state}`);

          if (state === 'connected') {
            // Clear retry state and dismiss toast on successful connection
            const retryEntry = retryStateRef.current.get(camId);
            if (retryEntry?.timeoutId) clearTimeout(retryEntry.timeoutId);
            retryStateRef.current.delete(camId);
            const countdownId = countdownIntervalsRef.current.get(camId);
            if (countdownId) {
              clearInterval(countdownId);
              countdownIntervalsRef.current.delete(camId);
            }
            removeToastForCamera(camId);

          } else if (state === 'disconnected') {
            // ─── FIX 3: Do NOT retry on 'disconnected' ──────────────────────
            // 'disconnected' is transient — WebRTC will self-recover to
            // 'connected' if the network blip resolves within ~5 seconds.
            // Tearing down the PC here is what was causing the 1-second drop.
            // Only log it; let the browser handle recovery.
            console.log(`[admin] Camera ${camId} temporarily disconnected — waiting for self-recovery`);

          } else if (state === 'failed') {
            // ─── Only retry on 'failed' — this is unrecoverable ─────────────
            peerConnectionsRef.current.delete(camId);
            setRemoteStreams((prev) => {
              const next = new Map(prev);
              next.delete(camId);
              remoteStreamsRef.current = next;
              return next;
            });
            setLiveCameras((prev) => {
              const next = new Set(prev);
              next.delete(camId);
              return next;
            });

            if (!cameraInstanceIdRef.current) return;

            const MAX_ATTEMPTS = 5;
            const BASE_DELAY_MS = 2000;
            const MAX_DELAY_MS = 30000;

            const existing = retryStateRef.current.get(camId);
            const attempt = existing ? existing.attempt + 1 : 1;

            if (attempt > MAX_ATTEMPTS) {
              retryStateRef.current.delete(camId);
              console.warn(`[admin] Max retry attempts reached for camera ${camId}`);
              showFailedToast(camId, MAX_ATTEMPTS);
              return;
            }

            const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt - 1), MAX_DELAY_MS);
            const delaySeconds = Math.round(delay / 1000);
            console.log(`[admin] Retrying ping to camera ${camId} in ${delay}ms (attempt ${attempt}/${MAX_ATTEMPTS})`);

            startCountdown(camId, delaySeconds, attempt, MAX_ATTEMPTS);

            const timeoutId = setTimeout(() => {
              const sendPing = sendPingRef.current.get(camId);
              if (sendPing && cameraInstanceIdRef.current) {
                sendPing(cameraInstanceIdRef.current);
              } else {
                retryStateRef.current.delete(camId);
              }
            }, delay);

            retryStateRef.current.set(camId, { attempt, timeoutId });

          } else if (state === 'closed') {
            peerConnectionsRef.current.delete(camId);
            setRemoteStreams((prev) => {
              const next = new Map(prev);
              next.delete(camId);
              remoteStreamsRef.current = next;
              return next;
            });
            setLiveCameras((prev) => {
              const next = new Set(prev);
              next.delete(camId);
              return next;
            });
          }
        };

        peerConnectionsRef.current.set(camId, pc);
        return pc;
      };

      const sendPing = (cameraInstanceId: string) => {
        channel.send({
          type: 'broadcast',
          event: 'signal',
          payload: { type: 'ping', from: myId.current, to: cameraInstanceId },
        });
      };
      sendPingRef.current.set(cameraId, sendPing);

      channel
        .on('broadcast', { event: 'signal' }, async ({ payload }) => {
          const msg = payload as {
            type: string;
            sdp?: string;
            candidate?: RTCIceCandidateInit;
            from: string;
            to?: string;
            cameraId?: string;
          };

          if (msg.to && msg.to !== myId.current) return;
          if (msg.from === myId.current) return;

          if (msg.type === 'camera-online') {
            cameraInstanceIdRef.current = msg.from;
            setLiveCameras((prev) => new Set([...prev, cameraId]));

            // Only ping if we don't already have a stream for this camera
            // Use remoteStreamsRef (always current) — NOT inside a state updater
            if (!remoteStreamsRef.current.has(cameraId)) {
              sendPing(msg.from);
            }

          } else if (msg.type === 'offer') {
            // ─── FIX 4: Negotiation guard — ignore duplicate offers ──────────
            if (isNegotiatingRef.current.get(cameraId)) {
              console.log(`[admin] Camera ${cameraId} already negotiating — ignoring duplicate offer`);
              return;
            }
            isNegotiatingRef.current.set(cameraId, true);

            cameraInstanceIdRef.current = msg.from;

            const pc = createPeerConnection(cameraId);
            try {
              await pc.setRemoteDescription(
                new RTCSessionDescription({ type: 'offer', sdp: msg.sdp })
              );

              // ─── FIX 5: Flush queued ICE candidates after remote desc set ──
              const queued = iceCandidateQueueRef.current.get(cameraId) ?? [];
              for (const candidate of queued) {
                try {
                  await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch {}
              }
              iceCandidateQueueRef.current.set(cameraId, []);

              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);

              channel.send({
                type: 'broadcast',
                event: 'signal',
                payload: {
                  type: 'answer',
                  sdp: answer.sdp,
                  from: myId.current,
                  to: msg.from,
                },
              });
            } catch (err) {
              console.error('[admin] offer/answer failed for cam', cameraId, err);
            } finally {
              // Always clear negotiation guard so future offers are accepted
              isNegotiatingRef.current.set(cameraId, false);
            }

          } else if (msg.type === 'ice-candidate') {
            const pc = peerConnectionsRef.current.get(cameraId);
            if (!pc) return;

            // ─── FIX 6: Queue ICE candidates if no remote description yet ───
            if (!pc.remoteDescription) {
              const queue = iceCandidateQueueRef.current.get(cameraId) ?? [];
              if (msg.candidate) queue.push(msg.candidate);
              iceCandidateQueueRef.current.set(cameraId, queue);
            } else if (msg.candidate) {
              try {
                await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
              } catch {}
            }
          }
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            channel.send({
              type: 'broadcast',
              event: 'signal',
              payload: { type: 'admin-online', from: myId.current },
            });
          }
        });

      channelsRef.current.set(cameraId, channel);
    },
    [startCountdown, showFailedToast, removeToastForCamera]
  );

  // Initialise connections when user logs in; tear down when user logs out
  useEffect(() => {
    const currentUserId = user?.id ?? null;

    if (!currentUserId && prevUserRef.current) {
      teardown();
      prevUserRef.current = null;
      return;
    }

    if (currentUserId && !initialisedRef.current) {
      initialisedRef.current = true;
      prevUserRef.current = currentUserId;
      CAMERAS.forEach((c) => setupCameraChannel(c.id));
    }
  }, [user, setupCameraChannel, teardown]);

  const toggleMute = useCallback((id: number) => {
    setMutedCameras((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const setVolume = useCallback((id: number, value: number) => {
    setCameraVolumes((prev) => ({ ...prev, [id]: value }));
  }, []);

  // ─── Now Live Camera — load from Supabase and persist on change ─────────────

  useEffect(() => {
    const supabase = supabaseRef.current;
    supabase
      .from('now_live_setting')
      .select('camera_id')
      .eq('id', 1)
      .single()
      .then(({ data }) => {
        if (data?.camera_id) {
          setNowLiveCameraState(data.camera_id);
          nowLiveCameraRef.current = data.camera_id;
        }
      });
  }, []);

  // ─── watch-live-signal relay ─────────────────────────────────────────────────
  // Listens for viewer pings, then relays the nowLive camera stream to the viewer
  useEffect(() => {
    if (!user) return;

    const supabase = supabaseRef.current;
    const channel = supabase.channel('watch-live-signal', {
      config: { broadcast: { self: false } },
    });
    watchLiveChannelRef.current = channel;

    channel
      .on('broadcast', { event: 'watch-signal' }, async ({ payload }) => {
        const msg = payload as {
          type: string;
          from: string;
          to?: string;
          sdp?: string;
          candidate?: RTCIceCandidateInit;
        };

        if (msg.from === myId.current) return;

        if (msg.type === 'viewer-ping') {
          const viewerId = msg.from;
          const camId = nowLiveCameraRef.current;
          const stream = remoteStreamsRef.current.get(camId);

          // Close any existing relay peer for this viewer
          const existing = relayPeersRef.current.get(viewerId);
          if (existing) {
            existing.onicecandidate = null;
            existing.onconnectionstatechange = null;
            existing.close();
            relayPeersRef.current.delete(viewerId);
          }
          // Clear any queued ICE candidates for this viewer
          relayIceCandidateQueueRef.current.delete(viewerId);

          if (!stream) {
            // No stream available yet — send a 'no-stream' notice
            channel.send({
              type: 'broadcast',
              event: 'watch-signal',
              payload: { type: 'no-stream', from: myId.current, to: viewerId },
            });
            return;
          }

          // Check that the stream has active tracks before relaying
          const activeTracks = stream.getTracks().filter((t) => t.readyState === 'live');
          if (activeTracks.length === 0) {
            channel.send({
              type: 'broadcast',
              event: 'watch-signal',
              payload: { type: 'no-stream', from: myId.current, to: viewerId },
            });
            return;
          }

          const pc = new RTCPeerConnection({
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' },
              { urls: 'stun:stun2.l.google.com:19302' },
              { urls: 'turn:openrelay.metered.ca:80',                username: 'openrelayproject', credential: 'openrelayproject' },
              { urls: 'turn:openrelay.metered.ca:443',               username: 'openrelayproject', credential: 'openrelayproject' },
              { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
            ],
            iceCandidatePoolSize: 10,
          });
          relayPeersRef.current.set(viewerId, pc);
          relayIceCandidateQueueRef.current.set(viewerId, []);

          // Add only live tracks from the stream
          activeTracks.forEach((track) => pc.addTrack(track, stream));

          pc.onicecandidate = (event) => {
            if (event.candidate) {
              channel.send({
                type: 'broadcast',
                event: 'watch-signal',
                payload: {
                  type: 'ice-candidate',
                  candidate: event.candidate.toJSON(),
                  from: myId.current,
                  to: viewerId,
                },
              });
            }
          };

          pc.onconnectionstatechange = () => {
            const state = pc.connectionState;
            console.log(`[relay] viewer ${viewerId} state: ${state}`);
            if (state === 'failed' || state === 'closed') {
              pc.onicecandidate = null;
              pc.onconnectionstatechange = null;
              pc.close();
              relayPeersRef.current.delete(viewerId);
              relayIceCandidateQueueRef.current.delete(viewerId);
            }
          };

          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            channel.send({
              type: 'broadcast',
              event: 'watch-signal',
              payload: {
                type: 'offer',
                sdp: offer.sdp,
                from: myId.current,
                to: viewerId,
              },
            });
          } catch (err) {
            console.error('[relay] failed to create offer for viewer', viewerId, err);
            pc.close();
            relayPeersRef.current.delete(viewerId);
            relayIceCandidateQueueRef.current.delete(viewerId);
          }

        } else if (msg.type === 'answer') {
          if (!msg.to || msg.to !== myId.current) return;
          const pc = relayPeersRef.current.get(msg.from);
          if (!pc || !msg.sdp) return;
          try {
            await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: msg.sdp }));
            // Flush any queued ICE candidates
            const queued = relayIceCandidateQueueRef.current.get(msg.from) ?? [];
            for (const candidate of queued) {
              try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
            }
            relayIceCandidateQueueRef.current.set(msg.from, []);
          } catch (err) {
            console.error('[relay] setRemoteDescription failed', err);
          }

        } else if (msg.type === 'ice-candidate') {
          if (!msg.to || msg.to !== myId.current) return;
          const pc = relayPeersRef.current.get(msg.from);
          if (!pc || !msg.candidate) return;
          try {
            if (pc.remoteDescription) {
              await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
            } else {
              // Queue until answer is processed
              const queue = relayIceCandidateQueueRef.current.get(msg.from) ?? [];
              queue.push(msg.candidate);
              relayIceCandidateQueueRef.current.set(msg.from, queue);
            }
          } catch {}
        }
      })
      .subscribe((status) => {
        console.log('[relay] watch-live-signal channel status:', status);
      });

    return () => {
      channel.unsubscribe();
      watchLiveChannelRef.current = null;
      relayPeersRef.current.forEach((pc) => pc.close());
      relayPeersRef.current.clear();
      relayIceCandidateQueueRef.current.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Keep relay peers updated when nowLiveCamera changes — close stale relay peers
  // so next viewer-ping uses the new stream
  useEffect(() => {
    nowLiveCameraRef.current = nowLiveCamera;
    relayPeersRef.current.forEach((pc) => {
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      pc.close();
    });
    relayPeersRef.current.clear();
    relayIceCandidateQueueRef.current.clear();
  }, [nowLiveCamera]);

  const setNowLiveCamera = useCallback((id: number) => {
    setNowLiveCameraState(id);
    nowLiveCameraRef.current = id;
    supabaseRef.current
      .from('now_live_setting')
      .upsert({ id: 1, camera_id: id, updated_at: new Date().toISOString() })
      .then(() => {});
  }, []);

  return (
    <WebRTCContext.Provider
      value={{
        remoteStreams,
        liveCameras,
        mutedCameras,
        cameraVolumes,
        toasts,
        nowLiveCamera,
        dismissToast,
        toggleMute,
        setVolume,
        setNowLiveCamera,
      }}
    >
      {children}
    </WebRTCContext.Provider>
  );
}
